import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { listData, updateData, createData, deleteData, fmtMoney } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Target, CurrencyDollar, ChartLineUp } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

export default function RoasPage() {
  const { activeId } = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const key = ["data", "roas_entries", activeId];
  const { data: rows = [] } = useQuery({ queryKey: key, queryFn: () => listData("roas_entries", activeId), enabled: !!activeId });
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(null);
  const inv = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["dashboard", activeId] }); };

  const saveM = useMutation({ mutationFn: (d) => createData("roas_entries", { ...d, client_id: activeId }), onSuccess: () => { inv(); toast.success("Logged"); } });
  const delM = useMutation({ mutationFn: (id) => deleteData("roas_entries", id), onSuccess: () => { inv(); toast.success("Removed"); } });

  const spend = rows.reduce((s, r) => s + Number(r.spend || 0), 0);
  const revenue = rows.reduce((s, r) => s + Number(r.revenue || 0), 0);
  const conv = rows.reduce((s, r) => s + Number(r.conversions || 0), 0);
  const roas = spend ? (revenue / spend).toFixed(2) : "0";

  const byCh = {};
  rows.forEach((r) => {
    byCh[r.channel] = byCh[r.channel] || { channel: r.channel, spend: 0, revenue: 0 };
    byCh[r.channel].spend += Number(r.spend || 0);
    byCh[r.channel].revenue += Number(r.revenue || 0);
  });
  const chartData = Object.values(byCh);

  return (
    <PageContainer>
      <PageHeader eyebrow="Paid Performance" title="ROAS Tracker"
        subtitle="Log ad spend and attributed revenue per channel. Kill weak performers fast and move budget to winners.">
        <Button data-testid="add-roas-btn" onClick={() => { setCur({ date: new Date().toISOString().slice(0, 10), channel: "", spend: 0, revenue: 0, conversions: 0 }); setOpen(true); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Plus size={16} className="mr-1.5" /> Log entry</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard label="Blended ROAS" value={`${roas}x`} icon={Target} accent />
        <StatCard label="Total Spend" value={fmtMoney(spend)} icon={CurrencyDollar} />
        <StatCard label="Attributed Revenue" value={fmtMoney(revenue)} icon={ChartLineUp} />
        <StatCard label="Conversions" value={conv} icon={Target} />
      </div>

      {rows.length === 0 ? (
        <div className="card-matte p-10 text-center" data-testid="roas-empty-state">
          <Target size={36} className="mx-auto text-[#5a5a5a] mb-3" />
          <div className="font-head font-semibold text-lg">No ROAS data yet</div>
          <p className="text-sm text-[#9a9a9a] mt-1 max-w-md mx-auto">Connect Meta Ads or Google Ads to view ad spend and ROAS — or log spend and revenue entries manually.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button data-testid="roas-empty-connect-btn" onClick={() => navigate("/ads")} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Connect Meta / Google Ads</Button>
            <Button data-testid="roas-empty-log-btn" variant="ghost" onClick={() => { setCur({ date: new Date().toISOString().slice(0, 10), channel: "", spend: 0, revenue: 0, conversions: 0 }); setOpen(true); }} className="border border-white/15 text-white hover:bg-white/5"><Plus size={16} className="mr-1.5" /> Log entry manually</Button>
          </div>
        </div>
      ) : (
      <>
      <div className="card-matte p-5 mb-5">
        <div className="label-caps mb-4">Spend vs Revenue by Channel</div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ left: -10 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="channel" stroke="#5a5a5a" tick={{ fontSize: 10, fill: "#9a9a9a" }} />
            <YAxis stroke="#5a5a5a" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ background: "#10101A", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10 }} formatter={(v) => fmtMoney(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="spend" name="Spend" fill="#3a3a3a" radius={[4, 4, 0, 0]} />
            <Bar dataKey="revenue" name="Revenue" fill="#6B3DFF" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card-matte overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                {["Date", "Channel", "Spend", "Revenue", "Conv.", "ROAS", ""].map((h) => <TableHead key={h} className="label-caps text-[#7a7a7a]">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="border-white/8 hover:bg-white/[0.03]" data-testid="roas-row">
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{r.channel}</TableCell>
                  <TableCell>{fmtMoney(r.spend)}</TableCell>
                  <TableCell>{fmtMoney(r.revenue)}</TableCell>
                  <TableCell>{r.conversions}</TableCell>
                  <TableCell className="brand-text font-semibold">{r.spend ? (r.revenue / r.spend).toFixed(2) : "0"}x</TableCell>
                  <TableCell className="text-right"><button onClick={() => delM.mutate(r.id)} className="text-[#9a9a9a] hover:text-red-400 p-1.5"><Trash size={15} /></button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-head">Log ROAS entry</DialogTitle></DialogHeader>
          {cur && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">Date</Label><Input type="date" value={cur.date} onChange={(e) => setCur({ ...cur, date: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" /></div>
                <div><Label className="label-caps">Channel</Label><Input value={cur.channel} onChange={(e) => setCur({ ...cur, channel: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" data-testid="roas-channel" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="label-caps">Spend</Label><Input type="number" value={cur.spend} onChange={(e) => setCur({ ...cur, spend: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" /></div>
                <div><Label className="label-caps">Revenue</Label><Input type="number" value={cur.revenue} onChange={(e) => setCur({ ...cur, revenue: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" /></div>
                <div><Label className="label-caps">Conv.</Label><Input type="number" value={cur.conversions} onChange={(e) => setCur({ ...cur, conversions: Number(e.target.value) })} className="bg-[#10101A] border-white/10 mt-1" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="save-roas-btn" onClick={() => { saveM.mutate(cur); setOpen(false); }} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
